import { Link, useLocation, useNavigate } from 'react-router-dom';

import { parentRoute } from '@/lib/navigation';
import LangToggle from './LangToggle';
import MuteButton from './MuteButton';

/**
 * Compact translucent top bar — present on every screen.
 * Holds the logo (home link), an optional back arrow on sub-pages,
 * the language switcher, and the mute button.
 */
export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();

  // Show back arrow on every screen except the home page and onboarding
  // (onboarding is a forced flow — back would dump the user into an
  // empty-profile state).
  const isHome = location.pathname === '/';
  const isOnboarding = location.pathname === '/onboarding';
  const showBack = !isHome && !isOnboarding;

  const goBack = () => {
    // Parent-aware: jump to the logical parent of the current route,
    // not whichever screen happened to be last in browser history.
    // This stops "5-clicks-to-Home" syndrome on long sessions.
    navigate(parentRoute(location.pathname));
  };

  return (
    <header className="sticky top-0 z-20 mx-auto flex max-w-md items-center
                       justify-between px-5 pt-3 pb-2 backdrop-blur-sm">
      <div className="flex items-center gap-2">
        {showBack && (
          <button
            type="button"
            onClick={goBack}
            aria-label="Back"
            className="flex h-11 w-11 items-center justify-center rounded-full
                       border border-gold-500/40 bg-night-900/50 text-xl text-gold-200
                       shadow-[0_0_12px_rgba(232,195,86,0.15)]
                       transition active:scale-95
                       hover:border-gold-400 hover:text-gold-100"
          >
            ←
          </button>
        )}
        <Link to="/" className="group flex items-center gap-2">
          <span className="text-2xl text-gold-400 transition group-hover:text-gold-300">
            ✦
          </span>
          <span className="font-serif text-lg tracking-wide text-gold-300">
            Mystic Companion
          </span>
        </Link>
      </div>
      <div className="flex items-center">
        <LangToggle />
        <MuteButton />
      </div>
    </header>
  );
}
